import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';
import disclaimerForMarket from '../utils/disclaimerForMarket';
import { hasScrolledToEnd } from '../utils/scrollToEnd';

const DisclaimerB4LogIn = ({ open, onAccept, onExit, config, market, marketResolved = true }) => {

    // Which document this customer must agree to, chosen by their own market rather than by the
    // deployment the bundle was built for.
    const activeConfig = disclaimerForMarket(market, config);

    // Accept stays disabled until the terms have been scrolled to the end.
    const contentRef = useRef(null);
    const [readToEnd, setReadToEnd] = useState(false);

    const evaluateScroll = useCallback(() => {
        setReadToEnd(hasScrolledToEnd(contentRef.current));
    }, []);

    // The document to be read changes once the market resolves, and its length decides whether
    // scrolling is needed at all, so re-measure whenever it does.
    const textLength = activeConfig?.disclaimerText?.length ?? 0;

    useEffect(() => {
        if (!open || !marketResolved) {
            // Re-arm for next time: a dialog reopened after Exit must not still be satisfied by a
            // scroll from the previous visit.
            setReadToEnd(false);
            return undefined;
        }
        // Measured after paint, not during it. On the render that mounts the dialog the content
        // has no layout yet, so scrollHeight and clientHeight are both 0 — which reads as "fits,
        // nothing to scroll" and would enable the button immediately, every time.
        const frame = requestAnimationFrame(evaluateScroll);
        // A window resize can turn a scrolling document into one that fits, and no scroll event
        // fires for that.
        window.addEventListener('resize', evaluateScroll);
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('resize', evaluateScroll);
        };
    }, [open, marketResolved, textLength, evaluateScroll]);

    const processDisclaimerText = (text) => {
        if (!text || !activeConfig) return '';
        return text
            .replace(/\{\[ASTROSTOCKCHARTS\.COM\]\}/g, activeConfig.siteName)
            .replace(/\{\{COMPANY_NAME\}\}/g, activeConfig.companyName);
    };

    // The profile carrying the market lands a moment after login resolves. Until it does, show
    // nothing rather than the default — a customer must never be shown, and never accept, a
    // disclaimer written for a market that is not theirs, even briefly.
    if (open && !marketResolved) {
        return (
            <Dialog open={open} onClose={onExit} maxWidth="md" fullWidth>
                <DialogTitle>Disclaimer and Terms of Use</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
                        <CircularProgress size={22} />
                        <Typography>Loading the terms that apply to your account...</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onExit} color="primary">Exit</Button>
                    <Button disabled variant="contained">Accept and Continue</Button>
                </DialogActions>
            </Dialog>
        );
    }

    const disclaimerText = activeConfig?.disclaimerText || [
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. {[ASTROSTOCKCHARTS.COM]} is a tool for educational purposes only. The information provided herein is not intended as financial advice.",
        "Vivamus magna justo, lacinia eget consectetur sed, convallis at tellus. {{COMPANY_NAME}} shall not be responsible for any trading decisions made based on the information provided.",
        "Curabitur non nulla sit amet nisl tempus convallis quis ac lectus. By clicking 'Accept and Continue', you acknowledge that you have read and understood this disclaimer.",
        "Donec sollicitudin molestie malesuada. All data is provided 'as is' without warranty of any kind."
    ];

    return (
        <Dialog open={open} onClose={onExit} maxWidth="md" fullWidth>
            <DialogTitle>Disclaimer and Terms of Use</DialogTitle>
            <DialogContent dividers ref={contentRef} onScroll={evaluateScroll}>
                <DialogContentText component="div">
                    {disclaimerText.map((paragraph, index) => (
                        <Typography key={index} paragraph>
                            {processDisclaimerText(paragraph)}
                        </Typography>
                    ))}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                {/* Sits beside the buttons rather than after the last paragraph, because it has to
                    be readable BEFORE scrolling — an instruction to scroll down that only appears
                    once you have scrolled down explains nothing. */}
                <Box sx={{ flex: 1, pl: 1 }}>
                    {!readToEnd && (
                        <Typography variant="body2" color="text.secondary">
                            [Read and scroll down to activate &quot;Accept and Continue&quot; button]
                        </Typography>
                    )}
                </Box>
                <Button onClick={onExit} color="primary">
                    Exit
                </Button>
                <Button
                    onClick={onAccept}
                    color="primary"
                    variant="contained"
                    disabled={!readToEnd}
                >
                    Accept and Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
};

DisclaimerB4LogIn.propTypes = {
    open: PropTypes.bool.isRequired,
    onAccept: PropTypes.func.isRequired,
    onExit: PropTypes.func.isRequired,
    config: PropTypes.object,
    /** The signed-in customer's marketId (or market code); selects which document is shown. */
    market: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** False while the profile carrying the market is still loading. */
    marketResolved: PropTypes.bool,
};

export default DisclaimerB4LogIn;
